import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, KeyRound } from "lucide-react";
import {
  LoginContainer,
  LoginContent,
  LogoWrapper,
  Title,
  Subtitle,
  ButtonGroup,
  LoginButton,
  GoogleIcon,
  ErrorMessage,
  Spinner,
} from "./index.styled";
import {
  getGoogleOAuthURL,
  storeOAuthState,
  passkeyLoginBegin,
  passkeyLoginFinish,
  saveTokenFromLogin,
  markPasskeyLoginSource,
} from "../../utils/apiService";
import { store } from "../../store";
import { clearData } from "../../store/dashboardSlice";
import {
  prepareCredentialRequestOptions,
  serializeCredential,
} from "../../utils/webauthn";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Check for error passed back from the callback page
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const callbackError = urlParams.get("error");

    if (callbackError) {
      setError(callbackError);
      // Clean the URL
      navigate("/login", { replace: true });
    }
  }, [location.search, navigate]);

  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);

    try {
      const redirectUri = window.location.origin + "/auth/callback";
      const { url, state } = await getGoogleOAuthURL(redirectUri);
      storeOAuthState(state);
      window.location.href = url;
    } catch (err) {
      console.error("Google login error:", err);
      setError("לא ניתן להתחבר עם גוגל כרגע. נסה שוב.");
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError(null);
    setLoading(true);

    try {
      // Step 1: Get challenge from server
      const options = await passkeyLoginBegin();

      // Step 2: Ask browser for credential
      const optionsInput = (options.publicKey || options) as unknown as Parameters<typeof prepareCredentialRequestOptions>[0];
      const publicKeyOptions = prepareCredentialRequestOptions(optionsInput);
      const credential = (await navigator.credentials.get({
        publicKey: publicKeyOptions,
      })) as PublicKeyCredential | null;

      if (!credential) {
        setError("הפעולה בוטלה");
        setLoading(false);
        return;
      }

      // Step 3: Send credential to server
      const serialized = serializeCredential(credential);
      const result = await passkeyLoginFinish(serialized);

      // Step 4: Save token, mark as passkey login, and redirect
      saveTokenFromLogin(result.token);
      markPasskeyLoginSource();
      store.dispatch(clearData());
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Passkey login error:", err);
      if (err instanceof DOMException && err.name === "NotAllowedError") {
        setError("הפעולה בוטלה");
      } else {
        setError("ההתחברות נכשלה. נסה שוב.");
      }
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginContent>
        <LogoWrapper>
          <Lock size={32} color="white" />
        </LogoWrapper>

        <Title>ברוכים הבאים</Title>
        <Subtitle>התחבר כדי לגשת לדשבורד</Subtitle>

        <ButtonGroup>
          <LoginButton
            $variant="google"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            <GoogleIcon viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </GoogleIcon>
            התחבר עם גוגל
          </LoginButton>

          <LoginButton
            $variant="passkey"
            onClick={handlePasskeyLogin}
            disabled={loading}
          >
            {loading ? (
              <Spinner />
            ) : (
              <KeyRound size={20} />
            )}
            {loading ? "מתחבר..." : "התחבר עם מפתח גישה"}
          </LoginButton>
        </ButtonGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </LoginContent>
    </LoginContainer>
  );
};

export default Login;
